import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Alert,
    Button,
    Card,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined
} from '@ant-design/icons';
import Layout from './Layout';
import {
    FIELD_VALUE_TYPES,
    fieldTypeLabel,
    tmApi
} from '../utils/taskManager';

const { Title, Text } = Typography;
const { TextArea } = Input;

const SUB_FIELD_TYPES = FIELD_VALUE_TYPES.filter(
    item =>
        !['composite', 'repeater'].includes(item.value)
);

const TaskManagerFieldsPage = () => {
    const [loading, setLoading] = useState(false);
    const [fields, setFields] = useState([]);
    const [groups, setGroups] = useState([]);
    const [dictionaries, setDictionaries] = useState([]);
    const [search, setSearch] = useState('');
    const [kindFilter, setKindFilter] = useState('all');
    const [open, setOpen] = useState(false);
    const [groupOpen, setGroupOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [editingGroup, setEditingGroup] = useState(null);
    const [form] = Form.useForm();
    const [groupForm] = Form.useForm();
    const valueType = Form.useWatch('value_type', form);
    const fieldKind = Form.useWatch('field_kind', form);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [fieldsRes, dictRes, groupsRes] =
                await Promise.all([
                    tmApi.get('/api/task-manager/fields'),
                    tmApi.get('/api/task-manager/dictionaries'),
                    tmApi.get('/api/task-manager/field-groups')
                ]);
            setFields(fieldsRes.data.data || []);
            setDictionaries(dictRes.data.data || []);
            setGroups(groupsRes.data.data || []);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки полей'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return fields.filter(item => {
            if (
                kindFilter !== 'all' &&
                item.field_kind !== kindFilter
            ) {
                return false;
            }
            if (!q) return true;
            return (
                String(item.system_name)
                    .toLowerCase()
                    .includes(q) ||
                String(item.human_name)
                    .toLowerCase()
                    .includes(q) ||
                String(item.value_type)
                    .toLowerCase()
                    .includes(q) ||
                String(item.group?.human_name || '')
                    .toLowerCase()
                    .includes(q)
            );
        });
    }, [fields, search, kindFilter]);

    const dictOptions = useMemo(
        () =>
            dictionaries.map(item => ({
                value: item.id,
                label: `${item.human_name} (${item.system_name})`
            })),
        [dictionaries]
    );

    const groupOptions = useMemo(
        () =>
            groups.map(item => ({
                value: item.id,
                label: item.human_name
            })),
        [groups]
    );

    const reloadDictionaries = async () => {
        try {
            const { data } = await tmApi.get(
                '/api/task-manager/dictionaries'
            );
            setDictionaries(data.data || []);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Не удалось загрузить справочники'
            );
        }
    };

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({
            field_kind: 'custom',
            value_type: 'string',
            is_active: true,
            is_multiple: false,
            sort_order: 500,
            group_id: groups.find(
                g => g.system_name === 'extra'
            )?.id,
            config: {
                sub_fields: [],
                item_label: 'Элемент',
                min_items: 0
            }
        });
        reloadDictionaries();
        setOpen(true);
    };

    const openEdit = record => {
        setEditing(record);
        const config = record.config || {};
        form.setFieldsValue({
            ...record,
            dictionary_id: record.dictionary_id || undefined,
            group_id: record.group_id || undefined,
            possible_values: Array.isArray(
                record.possible_values
            )
                ? record.possible_values.join('\n')
                : '',
            is_multiple: Boolean(record.is_multiple),
            config: {
                sub_fields: Array.isArray(config.sub_fields)
                    ? config.sub_fields.map(sub => ({
                          ...sub,
                          possible_values: Array.isArray(
                              sub.possible_values
                          )
                              ? sub.possible_values.join('\n')
                              : ''
                      }))
                    : [],
                item_label: config.item_label || 'Элемент',
                min_items: config.min_items || 0,
                max_items: config.max_items,
                placeholder: config.placeholder || ''
            }
        });
        reloadDictionaries();
        setOpen(true);
    };

    const openCreateGroup = () => {
        setEditingGroup(null);
        groupForm.resetFields();
        groupForm.setFieldsValue({
            sort_order: 100,
            is_active: true
        });
        setGroupOpen(true);
    };

    const openEditGroup = record => {
        setEditingGroup(record);
        groupForm.setFieldsValue(record);
        setGroupOpen(true);
    };

    const saveGroup = async () => {
        try {
            const values = await groupForm.validateFields();
            if (editingGroup) {
                await tmApi.put(
                    `/api/task-manager/field-groups/${editingGroup.id}`,
                    values
                );
                message.success('Группа обновлена');
            } else {
                await tmApi.post(
                    '/api/task-manager/field-groups',
                    values
                );
                message.success('Группа создана');
            }
            setGroupOpen(false);
            load();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения группы'
            );
        }
    };

    const removeGroup = async id => {
        try {
            await tmApi.delete(
                `/api/task-manager/field-groups/${id}`
            );
            message.success('Группа удалена');
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления группы'
            );
        }
    };

    const normalizeSubFields = list =>
        (list || [])
            .map(item => {
                if (!item?.key) return null;
                return {
                    key: String(item.key)
                        .trim()
                        .replace(/\s+/g, '_'),
                    label: String(
                        item.label || item.key
                    ).trim(),
                    value_type: item.value_type || 'string',
                    possible_values: String(
                        item.possible_values || ''
                    )
                        .split('\n')
                        .map(v => v.trim())
                        .filter(Boolean),
                    dictionary_id: item.dictionary_id || null,
                    required: Boolean(item.required),
                    is_multiple: Boolean(item.is_multiple)
                };
            })
            .filter(Boolean);

    const save = async () => {
        try {
            const values = await form.validateFields();
            const type = values.value_type || 'string';
            const config = values.config || {};

            const payload = {
                ...values,
                field_kind:
                    values.field_kind === 'main'
                        ? 'main'
                        : 'custom',
                group_id: values.group_id || null,
                possible_values: String(
                    values.possible_values || ''
                )
                    .split('\n')
                    .map(item => item.trim())
                    .filter(Boolean),
                is_multiple: Boolean(values.is_multiple),
                dictionary_id:
                    type === 'dictionary'
                        ? values.dictionary_id
                        : null,
                config: {
                    placeholder: config.placeholder || '',
                    item_label: config.item_label || 'Элемент',
                    min_items: Number(config.min_items) || 0,
                    max_items:
                        config.max_items != null &&
                        config.max_items !== ''
                            ? Number(config.max_items)
                            : null,
                    sub_fields:
                        type === 'composite' ||
                        type === 'repeater'
                            ? normalizeSubFields(
                                  config.sub_fields
                              )
                            : []
                }
            };

            if (editing) {
                await tmApi.put(
                    `/api/task-manager/fields/${editing.id}`,
                    payload
                );
                message.success('Поле обновлено');
            } else {
                await tmApi.post(
                    '/api/task-manager/fields',
                    payload
                );
                message.success('Поле создано');
            }

            setOpen(false);
            load();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения'
            );
        }
    };

    const discover = async () => {
        setLoading(true);
        try {
            const { data } = await tmApi.post(
                '/api/task-manager/fields/discover',
                null,
                { params: { limit: 8000 } }
            );
            message.success(
                `Найдено ключей: ${data.discovered}, новых: ${data.created}, обновлено: ${data.updated}`
            );
            await load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сканирования'
            );
        } finally {
            setLoading(false);
        }
    };

    const remove = async id => {
        try {
            await tmApi.delete(
                `/api/task-manager/fields/${id}`
            );
            message.success('Поле удалено');
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления'
            );
        }
    };

    return (
        <Layout>
            <div style={{ padding: 16 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                    wrap
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Поля задач
                        </Title>
                        <Text type="secondary">
                            Основные и кастомные поля, группы,
                            типы и справочники
                        </Text>
                    </div>
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={load}
                        >
                            Обновить
                        </Button>
                        <Button onClick={openCreateGroup}>
                            Группа
                        </Button>
                        <Button onClick={discover}>
                            Подтянуть все из задач
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreate}
                        >
                            Новое поле
                        </Button>
                    </Space>
                </Space>

                <Card
                    size="small"
                    title="Группы полей"
                    style={{ marginBottom: 16 }}
                >
                    <Space wrap>
                        {groups.map(g => (
                            <Tag
                                key={g.id}
                                style={{ cursor: 'pointer' }}
                                onClick={() => openEditGroup(g)}
                            >
                                {g.human_name}
                                <Popconfirm
                                    title="Удалить группу?"
                                    onConfirm={e => {
                                        e?.stopPropagation?.();
                                        removeGroup(g.id);
                                    }}
                                    onClick={e =>
                                        e?.stopPropagation?.()
                                    }
                                >
                                    <DeleteOutlined
                                        style={{
                                            marginLeft: 6,
                                            color: '#cf1322'
                                        }}
                                        onClick={e =>
                                            e.stopPropagation()
                                        }
                                    />
                                </Popconfirm>
                            </Tag>
                        ))}
                        {!groups.length ? (
                            <Text type="secondary">
                                Групп пока нет — создайте первую
                            </Text>
                        ) : null}
                    </Space>
                </Card>

                <Space wrap style={{ marginBottom: 12 }}>
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder="Поиск по system/human/type/группе"
                        style={{ maxWidth: 420 }}
                        value={search}
                        onChange={e =>
                            setSearch(e.target.value)
                        }
                    />
                    <Select
                        value={kindFilter}
                        style={{ width: 160 }}
                        onChange={setKindFilter}
                        options={[
                            { value: 'all', label: 'Все виды' },
                            {
                                value: 'main',
                                label: 'Основные'
                            },
                            {
                                value: 'custom',
                                label: 'Кастомные'
                            }
                        ]}
                    />
                </Space>

                <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={filtered}
                    pagination={{ pageSize: 40 }}
                    columns={[
                        {
                            title: 'System',
                            dataIndex: 'system_name',
                            width: 180
                        },
                        {
                            title: 'Название',
                            dataIndex: 'human_name'
                        },
                        {
                            title: 'Вид',
                            dataIndex: 'field_kind',
                            width: 110,
                            render: v =>
                                v === 'main' ? (
                                    <Tag color="geekblue">
                                        основное
                                    </Tag>
                                ) : (
                                    <Tag>кастомное</Tag>
                                )
                        },
                        {
                            title: 'Группа',
                            width: 140,
                            render: (_, row) =>
                                row.group?.human_name || '—'
                        },
                        {
                            title: 'Тип',
                            dataIndex: 'value_type',
                            width: 180,
                            render: (v, row) => (
                                <Space size={4} wrap>
                                    <Tag>
                                        {fieldTypeLabel(v)}
                                    </Tag>
                                    {row.is_multiple ? (
                                        <Tag color="blue">
                                            multiple
                                        </Tag>
                                    ) : null}
                                </Space>
                            )
                        },
                        {
                            title: 'Справочник',
                            width: 160,
                            render: (_, row) =>
                                row.dictionary?.human_name ||
                                (row.dictionary_id
                                    ? `#${row.dictionary_id}`
                                    : '—')
                        },
                        {
                            title: 'Активно',
                            dataIndex: 'is_active',
                            width: 90,
                            render: v =>
                                v ? (
                                    <Tag color="green">да</Tag>
                                ) : (
                                    <Tag>нет</Tag>
                                )
                        },
                        {
                            title: '',
                            width: 120,
                            render: (_, row) => (
                                <Space>
                                    <Button
                                        size="small"
                                        icon={
                                            <EditOutlined />
                                        }
                                        onClick={() =>
                                            openEdit(row)
                                        }
                                    />
                                    {row.field_kind !==
                                    'main' ? (
                                        <Popconfirm
                                            title="Удалить поле?"
                                            onConfirm={() =>
                                                remove(row.id)
                                            }
                                        >
                                            <Button
                                                size="small"
                                                danger
                                                icon={
                                                    <DeleteOutlined />
                                                }
                                            />
                                        </Popconfirm>
                                    ) : null}
                                </Space>
                            )
                        }
                    ]}
                />

                <Modal
                    open={groupOpen}
                    title={
                        editingGroup
                            ? `Группа #${editingGroup.id}`
                            : 'Новая группа полей'
                    }
                    onCancel={() => setGroupOpen(false)}
                    onOk={saveGroup}
                    destroyOnClose
                >
                    <Form form={groupForm} layout="vertical">
                        <Form.Item
                            name="system_name"
                            label="System name"
                            rules={[{ required: true }]}
                        >
                            <Input
                                disabled={Boolean(editingGroup)}
                            />
                        </Form.Item>
                        <Form.Item
                            name="human_name"
                            label="Название"
                            rules={[{ required: true }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="sort_order"
                            label="Порядок"
                        >
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                            name="is_active"
                            label="Активна"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    open={open}
                    title={
                        editing
                            ? `Поле #${editing.id}`
                            : 'Новое поле'
                    }
                    onCancel={() => setOpen(false)}
                    onOk={save}
                    width={760}
                    destroyOnClose
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="field_kind"
                            label="Вид поля"
                            rules={[{ required: true }]}
                            extra={
                                fieldKind === 'main'
                                    ? 'Основное поле привязано к колонке задачи (system_name должен совпадать с колонкой).'
                                    : 'Кастомное поле хранится в JSON задачи.'
                            }
                        >
                            <Select
                                disabled={
                                    Boolean(editing) &&
                                    editing.field_kind ===
                                        'main'
                                }
                                options={[
                                    {
                                        value: 'custom',
                                        label: 'Кастомное'
                                    },
                                    {
                                        value: 'main',
                                        label: 'Основное'
                                    }
                                ]}
                            />
                        </Form.Item>
                        <Form.Item
                            name="group_id"
                            label="Группа"
                        >
                            <Select
                                allowClear
                                options={groupOptions}
                                placeholder="Без группы"
                            />
                        </Form.Item>
                        <Form.Item
                            name="system_name"
                            label="System name"
                            rules={[{ required: true }]}
                        >
                            <Input
                                disabled={Boolean(editing)}
                            />
                        </Form.Item>
                        <Form.Item
                            name="human_name"
                            label="Отображаемое имя"
                            rules={[{ required: true }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="value_type"
                            label="Тип поля"
                            rules={[{ required: true }]}
                            extra="Чтобы подтянуть значения из сущности — выберите тип «Справочник (привязка сущности)»"
                        >
                            <Select
                                options={FIELD_VALUE_TYPES}
                                onChange={nextType => {
                                    if (nextType !== 'dictionary') {
                                        form.setFieldValue(
                                            'dictionary_id',
                                            undefined
                                        );
                                    }
                                }}
                            />
                        </Form.Item>

                        {(valueType === 'select' ||
                            valueType === 'dictionary') && (
                            <Form.Item
                                name="is_multiple"
                                label="Множественный выбор"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        )}

                        {valueType === 'select' && (
                            <Form.Item
                                name="possible_values"
                                label="Значения списка (по строке)"
                            >
                                <TextArea rows={4} />
                            </Form.Item>
                        )}

                        {valueType === 'dictionary' && (
                            <Card
                                size="small"
                                title="Привязка справочника"
                                style={{ marginBottom: 16 }}
                                extra={
                                    <Space>
                                        <Button
                                            size="small"
                                            icon={
                                                <ReloadOutlined />
                                            }
                                            onClick={
                                                reloadDictionaries
                                            }
                                        >
                                            Обновить
                                        </Button>
                                        <Link to="/task-manager/dictionaries">
                                            Создать справочник
                                        </Link>
                                    </Space>
                                }
                            >
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 12 }}
                                    message="Поле будет выпадающим списком из записей выбранной сущности (код → в задачу, название → в списке)."
                                />
                                {!dictOptions.length ? (
                                    <Alert
                                        type="warning"
                                        showIcon
                                        style={{
                                            marginBottom: 12
                                        }}
                                        message="Справочников пока нет"
                                        description={
                                            <span>
                                                Сначала создайте
                                                сущность на странице{' '}
                                                <Link to="/task-manager/dictionaries">
                                                    Справочники
                                                </Link>
                                                , затем вернитесь и
                                                привяжите её здесь.
                                            </span>
                                        }
                                    />
                                ) : null}
                                <Form.Item
                                    name="dictionary_id"
                                    label="Справочник / сущность"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                'Выберите справочник'
                                        }
                                    ]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        optionFilterProp="label"
                                        options={dictOptions}
                                        placeholder="Например: Базовые станции"
                                        notFoundContent="Нет справочников"
                                    />
                                </Form.Item>
                            </Card>
                        )}

                        {(valueType === 'composite' ||
                            valueType === 'repeater') && (
                            <Card
                                size="small"
                                title={
                                    valueType === 'repeater'
                                        ? 'Схема элемента (динамическое поле)'
                                        : 'Подполя составного поля'
                                }
                                style={{ marginBottom: 12 }}
                            >
                                {valueType === 'repeater' && (
                                    <Space
                                        wrap
                                        style={{
                                            marginBottom: 12
                                        }}
                                    >
                                        <Form.Item
                                            name={[
                                                'config',
                                                'item_label'
                                            ]}
                                            label="Название элемента"
                                            style={{
                                                marginBottom: 0
                                            }}
                                        >
                                            <Input
                                                style={{
                                                    width: 160
                                                }}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            name={[
                                                'config',
                                                'min_items'
                                            ]}
                                            label="Мин."
                                            style={{
                                                marginBottom: 0
                                            }}
                                        >
                                            <InputNumber
                                                min={0}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            name={[
                                                'config',
                                                'max_items'
                                            ]}
                                            label="Макс."
                                            style={{
                                                marginBottom: 0
                                            }}
                                        >
                                            <InputNumber
                                                min={1}
                                            />
                                        </Form.Item>
                                    </Space>
                                )}

                                <Form.List
                                    name={[
                                        'config',
                                        'sub_fields'
                                    ]}
                                >
                                    {(
                                        subFields,
                                        { add, remove: removeSub }
                                    ) => (
                                        <>
                                            {subFields.map(
                                                field => (
                                                    <Card
                                                        key={
                                                            field.key
                                                        }
                                                        size="small"
                                                        style={{
                                                            marginBottom: 8
                                                        }}
                                                        extra={
                                                            <Button
                                                                type="text"
                                                                danger
                                                                icon={
                                                                    <DeleteOutlined />
                                                                }
                                                                onClick={() =>
                                                                    removeSub(
                                                                        field.name
                                                                    )
                                                                }
                                                            />
                                                        }
                                                    >
                                                        <Space
                                                            wrap
                                                            align="start"
                                                        >
                                                            <Form.Item
                                                                {...field}
                                                                name={[
                                                                    field.name,
                                                                    'key'
                                                                ]}
                                                                label="key"
                                                                rules={[
                                                                    {
                                                                        required: true
                                                                    }
                                                                ]}
                                                            >
                                                                <Input
                                                                    style={{
                                                                        width: 120
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                            <Form.Item
                                                                {...field}
                                                                name={[
                                                                    field.name,
                                                                    'label'
                                                                ]}
                                                                label="Название"
                                                            >
                                                                <Input
                                                                    style={{
                                                                        width: 140
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                            <Form.Item
                                                                {...field}
                                                                name={[
                                                                    field.name,
                                                                    'value_type'
                                                                ]}
                                                                label="Тип"
                                                                initialValue="string"
                                                            >
                                                                <Select
                                                                    style={{
                                                                        width: 170
                                                                    }}
                                                                    options={
                                                                        SUB_FIELD_TYPES
                                                                    }
                                                                />
                                                            </Form.Item>
                                                            <Form.Item
                                                                shouldUpdate
                                                                noStyle
                                                            >
                                                                {() => {
                                                                    const subType =
                                                                        form.getFieldValue(
                                                                            [
                                                                                'config',
                                                                                'sub_fields',
                                                                                field.name,
                                                                                'value_type'
                                                                            ]
                                                                        );
                                                                    if (
                                                                        subType ===
                                                                        'dictionary'
                                                                    ) {
                                                                        return (
                                                                            <Form.Item
                                                                                {...field}
                                                                                name={[
                                                                                    field.name,
                                                                                    'dictionary_id'
                                                                                ]}
                                                                                label="Справочник"
                                                                            >
                                                                                <Select
                                                                                    style={{
                                                                                        width: 180
                                                                                    }}
                                                                                    options={
                                                                                        dictOptions
                                                                                    }
                                                                                />
                                                                            </Form.Item>
                                                                        );
                                                                    }
                                                                    if (
                                                                        subType ===
                                                                        'select'
                                                                    ) {
                                                                        return (
                                                                            <Form.Item
                                                                                {...field}
                                                                                name={[
                                                                                    field.name,
                                                                                    'possible_values'
                                                                                ]}
                                                                                label="Опции"
                                                                            >
                                                                                <TextArea
                                                                                    rows={2}
                                                                                    style={{
                                                                                        width: 180
                                                                                    }}
                                                                                />
                                                                            </Form.Item>
                                                                        );
                                                                    }
                                                                    return null;
                                                                }}
                                                            </Form.Item>
                                                            <Form.Item
                                                                {...field}
                                                                name={[
                                                                    field.name,
                                                                    'is_multiple'
                                                                ]}
                                                                label="Multi"
                                                                valuePropName="checked"
                                                            >
                                                                <Switch />
                                                            </Form.Item>
                                                            <Form.Item
                                                                {...field}
                                                                name={[
                                                                    field.name,
                                                                    'required'
                                                                ]}
                                                                label="Обяз."
                                                                valuePropName="checked"
                                                            >
                                                                <Switch />
                                                            </Form.Item>
                                                        </Space>
                                                    </Card>
                                                )
                                            )}
                                            <Button
                                                type="dashed"
                                                block
                                                icon={
                                                    <PlusOutlined />
                                                }
                                                onClick={() =>
                                                    add({
                                                        value_type:
                                                            'string',
                                                        is_multiple: false,
                                                        required: false
                                                    })
                                                }
                                            >
                                                Добавить подполе
                                            </Button>
                                        </>
                                    )}
                                </Form.List>
                            </Card>
                        )}

                        <Form.Item
                            name={[
                                'config',
                                'placeholder'
                            ]}
                            label="Placeholder"
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="external_field_id"
                            label="External field_id"
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="sort_order"
                            label="Порядок"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="is_active"
                            label="Активно"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </Layout>
    );
};

export default TaskManagerFieldsPage;
